import { Injectable } from '@angular/core'
import { environment } from '@env/environment'
import { Chart, ChartConfiguration } from 'chart.js'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'

@Injectable({
  providedIn: 'root',
})
export class PdfService {
  private _margin = 14
  async generateDynamicPdf(title: string, indicators: any[], charts: any = null): Promise<jsPDF> {
    // Create new PDF document
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    })
    const blackColor = [0, 0, 0]
    const greyColor = [136, 136, 136]
    const uppercaseTitleColor = blackColor

    // Holds a reference to the vertical position of the "cursor" for writing the PDF
    let yPosition = 10

    // Add Header
    try {
      const img = await new Promise<HTMLImageElement>((resolve, reject) => {
        const image = new Image()
        image.onload = () => resolve(image)
        image.onerror = reject
        image.src = environment.logoUrl
      })
      const ratio = Math.min(50 / img.width, 10 / img.height)
      const width = ratio * img.width
      const height = ratio * img.height

      doc.addImage(img, 'PNG', 12, yPosition, width, height, title)
      yPosition += height
    } catch {
      yPosition += 4
      this._addText(doc, yPosition, title, 20, blackColor, 0, 0, 'left')
    }

    const url = window.location.origin
    const subtitle = 'Generated from ' + url
    yPosition = this._addText(doc, yPosition, subtitle, 12, greyColor, 0, 6, 'right')

    if (indicators.length > 0) {
      yPosition = this._addText(doc, yPosition, 'INDICATORS', 14, uppercaseTitleColor, 0, 0)
      doc.setDrawColor(uppercaseTitleColor[0], uppercaseTitleColor[1], uppercaseTitleColor[2])
      doc.line(
        this._margin,
        yPosition - 2,
        doc.internal.pageSize.getWidth() - this._margin,
        yPosition - 2,
      )
      yPosition = yPosition + 2

      // Get key dynamically
      const key = indicators.find((line) => {
        if (line['2']) {
          return line
        }
      })
      const key1 = key['1'].replace(/"/g, '')
      const key2 = key['2'].replace(/"/g, '')
      const key3 = key['3'].replace(/"/g, '')

      // Process each item in the data array
      for (let i = 0; i < indicators.length; i++) {
        const item = indicators[i]

        // Skip empty items
        if (!item || item === '') continue

        // Check if we need a new page
        if (yPosition > 250) {
          doc.addPage()
          yPosition = 20
        }

        // Display indicators in the Document
        if (item[key1] && !item[key2] && !item[key3]) {
          // Name
          yPosition = this._addText(doc, yPosition, item[key1], 12, blackColor, 4, 0, 'center')
        } else if (item['1'] == 'INPUT' || item['1'] == 'OUTPUT') {
          // Column Headers
          yPosition = this._addText(doc, yPosition, item['1'], 10, greyColor, 6, 0)
        } else if (item[key1] && (item[key2] !== undefined || item[key3] !== undefined)) {
          // DATA ROW
          // This is a data row - create a mini table
          const tableData = []
          const row = [item[key1] || '', item[key2] || '', item[key3] || '']
          tableData.push(row)

          yPosition = this._addTableRow(doc, yPosition, tableData)
        }
      }
    }

    if (charts && charts.length > 0) {
      yPosition = this._addText(doc, yPosition, 'CHARTS', 14, uppercaseTitleColor, 8, 0)
      doc.setDrawColor(uppercaseTitleColor[0], uppercaseTitleColor[1], uppercaseTitleColor[2])
      doc.line(
        this._margin,
        yPosition - 2,
        doc.internal.pageSize.getWidth() - this._margin,
        yPosition - 2,
      )
      yPosition = yPosition + 6
      // Loop through charts and add titles before each image
      for (let i = 0; i < charts.length; i++) {
        const chartConfig = charts[i]
        const imageCharts = await this._chartsToImage([chartConfig])
        const chartImage = imageCharts[0]

        const chartWidth = doc.internal.pageSize.getWidth() - this._margin * 4
        const chartHeight = chartWidth / 2

        // Add page if graph can not fit on page
        if (yPosition + chartHeight > 270) {
          doc.addPage()
          yPosition = 20
        }

        // Add resultTitle if exists
        if (chartConfig.resultTitle) {
          yPosition = this._addText(
            doc,
            yPosition,
            chartConfig.resultTitle,
            12,
            blackColor,
            0,
            2,
            'center',
          )
        }
        // Add title if exists
        if (chartConfig.title) {
          yPosition = this._addText(
            doc,
            yPosition,
            chartConfig.title,
            10,
            greyColor,
            0,
            0,
            'center',
          )
        }

        const pageWidth = doc.internal.pageSize.getWidth()
        const marginX = (pageWidth - chartWidth) / 2
        doc.addImage(chartImage, 'PNG', marginX, yPosition, chartWidth, chartHeight)
        yPosition += 80
      }
    }

    // Add footer with date and page number
    const today = new Date()
    const pageCount = doc.getNumberOfPages()
    const hours = String(today.getHours()).padStart(2, '0')
    const minutes = String(today.getMinutes()).padStart(2, '0')
    const datetime = today.toLocaleDateString('en-CH') + ' ' + hours + ':' + minutes
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i)
      doc.setFontSize(8)
      doc.setTextColor(150)

      doc.text(`${datetime}`, this._margin, doc.internal.pageSize.height - 10)
      doc.text(
        `Page ${i} of ${pageCount}`,
        doc.internal.pageSize.width - 30,
        doc.internal.pageSize.height - 10,
      )
    }

    return doc
  }

  /**
   * Adds the provided text to the jsPDF document passed as argument with styling options
   * @param document: jsPDF - The document on which the text should be rendered
   * @param yPosition: number - The Y position where the text should be rendered (on the current page)
   * @param text: string - The text to be rendered
   * @param font_size: number - The font size of the text
   * @param font_color: number[] - The color of the text as an array of 3 numbers (r, g, b)
   * @param pad_before: number - The blank space to be left out before the text
   * @param pad_after: number - The blank space to be left out after the text
   * @param align: 'left' | 'center' | 'right' | 'justify'
   * @returns number - A new yPosition to keep track of where the text is written
   */
  private _addText(
    document: jsPDF,
    yPosition: number,
    text: string,
    font_size: number = 12,
    font_color: number[] = [0, 0, 0],
    pad_before: number = 0,
    pad_after: number = 0,
    align: 'left' | 'center' | 'right' | 'justify' = 'left',
  ): number {
    yPosition += pad_before

    document.setFontSize(font_size)
    document.setFont('helvetica', 'bold')
    document.setTextColor(font_color[0], font_color[1], font_color[2])

    // Calculate available width (page width minus margins)
    const pageWidth = document.internal.pageSize.getWidth()
    const pageHorizCenter: number = pageWidth / 2
    let margin = this._margin // in case of align left
    const maxWidth = pageWidth - this._margin * 2

    if (align == 'right') {
      margin = pageWidth - margin
    } else if (align == 'center') {
      margin = pageHorizCenter
    }

    // Split text into lines that fit within the available width
    const lines = document.splitTextToSize(text, maxWidth)

    // Add each line to the document
    for (let i = 0; i < lines.length; i++) {
      document.text(lines[i], margin, yPosition, {
        align: align,
      })
      yPosition += 6 // Line height (adjust as needed)
    }

    return (yPosition += pad_after) // Return updated position for next content
  }

  /**
   * Adds the provided text to the jsPDF document passed as argument with styling options
   * @param document: jsPDF - The document on which the table should be rendered
   * @param yPosition: number - The Y position where the table should be rendered (on the current page)
   * @param dataLine: string[][] - A two dimensional array of strings representing rows and cells
   * @returns number - A new yPosition to keep track of where the text is written
   */
  private _addTableRow(document: jsPDF, yPosition: number, dataLine: any[]) {
    const dataToPush = [{ 0: dataLine[0][0], 1: dataLine[0][1] + ' ' + dataLine[0][2] }]
    const pageWidth = document.internal.pageSize.getWidth() - this._margin * 2
    const valueWidth = pageWidth / 2
    const nameIndicatorWidth = pageWidth / 2
    autoTable(document, {
      body: dataToPush,
      startY: yPosition,
      styles: {
        fontSize: 9,
        cellPadding: 2,
      },
      columnStyles: {
        0: { cellWidth: nameIndicatorWidth, fontStyle: 'normal' },
        1: { cellWidth: valueWidth, halign: 'left', fontStyle: 'bold' },
      },
      theme: 'striped',
      margin: { left: this._margin },
    })

    return (document as any).lastAutoTable.finalY + 3
  }

  /**
   * Generates an array of images from chartJS chart data
   * @param charts - Array of objects representing charts (JSON objects and no actual chartJS object)
   * @returns An array of base64 strings each representing a graph as a PNG image
   */
  private async _chartsToImage(charts: ChartConfiguration[]): Promise<string[]> {
    const imageCharts: string[] = []

    for (let i = 0; i < charts.length; i++) {
      try {
        // Create canvas
        const canvas = document.createElement('canvas')
        canvas.width = 800
        canvas.height = 400

        // Config from parameter
        const chartConfig: ChartConfiguration = {
          type: charts[i].type,
          data: {
            labels: charts[i].data.labels,
            datasets: charts[i].data.datasets,
          },
          options: {
            ...charts[i].options,
            responsive: false,
            maintainAspectRatio: false,
            animation: {
              duration: 0,
            },
          },
        }

        // create the chart
        const chartInstance = new Chart(canvas, chartConfig)

        // Wait for chart to render
        await new Promise((resolve) => {
          chartInstance.update()
          setTimeout(resolve, 100)
        })

        // Convert to base64
        const base64Image = canvas.toDataURL('image/png')
        imageCharts.push(base64Image)

        // destroy the chart
        chartInstance.destroy()
      } catch (error) {
        console.error(`Error converting chart ${i} to image:`, error)
        imageCharts.push('')
      }
    }

    return imageCharts
  }
}
